<template>
  <label
    :for="id"
    ref="elRef"
    class="wl-chip relative inline-flex cursor-pointer items-center justify-center gap-1 rounded-full text-sm font-medium transition"
    :class="[
      `type-${type}`,
      `variant-${variant}`,
      `size-${size}`,
      { 'is-disabled': isDisabled, 'is-active': modelValue },
    ]"
  >
    <span class="prefix" v-if="$slots.prefix">
      <slot name="prefix" />
    </span>
    <span v-if="label || $slots.default" class="truncate">
      <slot />
      {{ label }}
    </span>
    <WLIcon
      @click.prevent="handleRemoveClick"
      v-if="modelValue && isRemovable"
      :size="IconSize.SMALL"
      :name="IconName.NAVIGATION_CLOSE"
    />
    <input
      :id="id"
      :disabled="isDisabled"
      type="checkbox"
      :checked="modelValue"
      class="pointer-events-none absolute inset-0 cursor-pointer appearance-none outline-none"
      @change="handleChange"
    />
    <span class="suffix" v-if="$slots.suffix">
      <slot name="suffix" />
    </span>
  </label>
</template>

<script setup lang="ts">
import { useId, useTemplateRef } from "vue";
import { useWebHaptics } from "../../composables/use-web-haptics";
import { IconName, IconSize, WLIcon } from "./../icon";
import type { WLChipProps } from "./types";

const {
  label,
  type = "secondary",
  variant = "default",
  size = "md",
  isDisabled,
  isRemovable = true,
} = defineProps<WLChipProps>();

const id = useId();
const modelValue = defineModel<boolean>({ default: false });
const elRef = useTemplateRef("elRef");
const { triggerHaptic } = useWebHaptics();
const handleChange = (): void => {
  if (isDisabled) return;

  triggerHaptic("soft");
  modelValue.value = !modelValue.value;
};

const handleRemoveClick = (): void => {
  if (isDisabled) return;

  triggerHaptic("soft");
  modelValue.value = false;
};

defineExpose({ elRef });
</script>
<style>
@reference "@theme";

.wl-chip {
  &.size-sm {
    @apply h-6 px-1.5;
  }

  &.size-md {
    @apply h-8 px-3;
  }
  &.type-secondary {
    @apply text-emphasis;

    /* VARIANTS */
    &.variant-default {
      @apply bg-muted;

      &:not(.is-disabled) {
        &:hover {
          @apply bg-emphasis;
        }

        &:active {
          @apply bg-pressed;
        }
      }

      &.is-active {
        @apply bg-neutral text-inverted;
      }
    }

    &.variant-elevated {
      @apply bg-default shadow-down-sm border border-transparent;

      &:not(.is-disabled) {
        &:hover {
          @apply border-inverted bg-default border shadow-none;
        }

        &:active {
          @apply border-inverted bg-theme-transparent border shadow-none;
        }
      }

      &.is-active {
        @apply border-inverted;
      }
    }

    &.variant-outline {
      @apply border-emphasis border;

      &:not(.is-disabled) {
        &:hover {
          @apply border-inverted;
        }

        &:active {
          @apply border-neutral-emphasis bg-theme-transparent;
        }
      }

      &.is-active {
        @apply border-neutral-emphasis;
      }
    }
  }

  &:has(:disabled) {
    @apply cursor-not-allowed opacity-20;
  }

  &:has(:focus-visible) {
    @apply outline-accent outline outline-1 outline-offset-2;
  }
}
</style>
